// src/components/CommercialLeads.jsx
//
// The lead-assignment inbox: every lead (from the public "Book a demo"
// form or anywhere else) with a control to link it to an ambassador.
// This is the page CommercialGuide.jsx describes as missing - GET /leads
// didn't exist anywhere before this. Admin-only by design (see
// backend/routes/commercial/leads.ts's GET /leads comment): deciding who
// a lead belongs to is a dispatch decision, not something an ambassador
// does for themselves.
//
// Same auth+fetch boilerplate as AgentDetail.jsx / CommercialDashboard.jsx.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Paper, Stack, Chip, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, Link,
  Select, MenuItem, FormControl, InputLabel, CircularProgress,
} from '@mui/material';
import { initKeycloak } from '../keycloak';
import { commercialGet, commercialPut } from '../utils/commercialApi';
import { INK, statusColor, statusLabel } from '../utils/commercialColors';

const STATUSES = [
  'PROSPECT', 'CONTACTED', 'DEMO', 'TRIAL', 'ACTIVATED',
  'PAID', 'CANCELLATION_SCHEDULED', 'CHURNED', 'LOST',
];

// Where the lead came from: the landing page (?page=) and the ad tags
// (backend/utils/attribution.ts), e.g. « page borne · google · mot-clé : borne avis ».
function leadOrigin(l) {
  return [
    l.landing_page && `page ${l.landing_page}`,
    l.utm_source,
    l.utm_campaign && `campagne ${l.utm_campaign}`,
    l.utm_term && `mot-clé : ${l.utm_term}`,
    !l.utm_source && l.gclid && 'Google Ads',
  ].filter(Boolean).join(' · ');
}

export default function CommercialLeads() {
  const [kcInstance, setKcInstance] = useState(null);
  const [token, setToken] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const loginRequestedRef = useRef(false);

  const [statusFilter, setStatusFilter] = useState('');
  const [unassignedOnly, setUnassignedOnly] = useState(false);

  const [leads, setLeads] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);

  /* ─── Auth ─── */
  useEffect(() => {
    (async () => {
      const kc = await initKeycloak();
      window.history.replaceState(null, '', window.location.origin + window.location.pathname);

      if (!kc.authenticated) {
        if (loginRequestedRef.current) return;
        loginRequestedRef.current = true;
        kc.login({ redirectUri: window.location.href });
        return;
      }
      // GET /leads is admin-only server side; this is defense-in-depth,
      // not the real boundary (same convention as every other page here).
      if (!kc.hasRealmRole('ROLE_ADMIN')) {
        setForbidden(true);
        setAuthLoading(false);
        return;
      }
      setKcInstance(kc);
      setToken(kc.token ?? '');
      setAuthLoading(false);
    })().catch(console.error);
  }, []);

  /* ─── Data ─── */
  const loadLeads = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError('');
    commercialGet('/leads', token, {
      status: statusFilter || undefined,
      unassigned: unassignedOnly ? 'true' : undefined,
    })
      .then((data) => setLeads(data.leads || []))
      .catch((err) => setError(err.message || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [token, statusFilter, unassignedOnly]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  useEffect(() => {
    if (!token) return;
    commercialGet('/agents', token)
      .then((data) => setAgents((data.agents || []).filter((a) => a.active)))
      .catch(() => {}); // non-critical for the page to still work
  }, [token]);

  const assignAgent = async (leadId, agentId) => {
    setSavingId(leadId);
    setSavedId(null);
    try {
      await commercialPut(`/leads/${leadId}`, token, { commercial_agent_id: agentId });
      setLeads((prev) =>
        prev.map((l) => {
          if (l.id !== leadId) return l;
          const agent = agents.find((a) => a.id === agentId);
          return {
            ...l,
            commercial_agent_id: agentId,
            agent_first_name: agent?.first_name ?? null,
            agent_last_name: agent?.last_name ?? null,
          };
        }),
      );
      setSavedId(leadId);
      setTimeout(() => setSavedId((cur) => (cur === leadId ? null : cur)), 2000);
    } catch (err) {
      setError(err.message || 'Échec de l’attribution');
    } finally {
      setSavingId(null);
    }
  };

  if (authLoading) {
    return <Typography sx={{ mt: 4, textAlign: 'center' }}>Chargement…</Typography>;
  }
  if (forbidden) {
    return <Typography sx={{ mt: 4, textAlign: 'center' }}>Accès refusé.</Typography>;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Link component={RouterLink} to="/admin/commercial" sx={{ display: 'block', mb: 1 }}>
            ← Retour au dashboard
          </Link>
          <Typography variant="h4">Attribution des leads</Typography>
        </Box>
        <Button variant="outlined" onClick={() => kcInstance?.logout({ redirectUri: `${window.location.origin}/` })}>
          Logout
        </Button>
      </Box>
      <Typography sx={{ color: INK.muted, mb: 3, maxWidth: 640 }}>
        Chaque lead (formulaire « Book a demo » ou autre) et l’ambassadeur auquel il est actuellement lié.
        Changer la sélection attribue le lead immédiatement.
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Statut</InputLabel>
          <Select value={statusFilter} label="Statut" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">Tous</MenuItem>
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant={unassignedOnly ? 'contained' : 'outlined'}
          onClick={() => setUnassignedOnly((v) => !v)}
        >
          Non assignés uniquement
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${INK.gridline}`, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Établissement</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Créé le</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Ambassadeur</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && leads && leads.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: INK.muted }}>
                  Aucun lead pour ces filtres.
                </TableCell>
              </TableRow>
            )}
            {!loading && leads && leads.map((l) => (
              <TableRow key={l.id}>
                <TableCell sx={{ fontWeight: 600 }}>
                  {l.business_name}
                  {leadOrigin(l) && (
                    <Box sx={{ fontSize: 12, fontWeight: 400, color: INK.muted }}>Origine : {leadOrigin(l)}</Box>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>
                  <div>{l.contact_name || '—'}</div>
                  <div style={{ color: INK.muted }}>{l.contact_email || '—'}</div>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={statusLabel(l.status)}
                    sx={{ bgcolor: statusColor(l.status), color: '#fff', fontWeight: 700 }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: 13, color: INK.muted, whiteSpace: 'nowrap' }}>
                  {l.created_at ? new Date(l.created_at).toLocaleDateString('fr-FR') : '—'}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <Select
                        value={l.commercial_agent_id ?? ''}
                        displayEmpty
                        disabled={savingId === l.id}
                        onChange={(e) => assignAgent(l.id, e.target.value === '' ? null : Number(e.target.value))}
                      >
                        <MenuItem value="">Non assigné</MenuItem>
                        {agents.map((a) => (
                          <MenuItem key={a.id} value={a.id}>
                            {a.first_name} {a.last_name}{a.zone ? ` — ${a.zone}` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {savingId === l.id && <CircularProgress size={16} />}
                    {savedId === l.id && <Typography sx={{ color: INK.success, fontSize: 13 }}>✓</Typography>}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Container>
  );
}
